import { useToast } from '@chakra-ui/react';
import {
  GLP1_OFFER_COPY,
  GLP1_PROGRAM_RESOURCE_LINKS,
  Glp1OfferVariant,
} from '@web-app/components/CallV2/SessionPrepTab/glp1-program-card.constants';
import { resolveCrossSellSendOutcome } from '@web-app/components/CallV2/SessionPrepTab/resolveCrossSellBookingLinkModel';
import { SessionPrepCardSkeleton } from '@web-app/components/CallV2/SessionPrepTab/SessionPrepCardSkeleton';
import { Button } from '@web-app/components/ui/Button/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@web-app/components/ui/Collapsible/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@web-app/components/ui/Tooltip/tooltip';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import {
  PlatformCrossSellChatDeliveryStatus,
  PlatformCrossSellSmsDeliveryStatus,
  PlatformCrossSellTargetProgram,
} from '@web-app/generated/platform/graphql';
import { useCrossSellBookingLink } from '@web-app/hooks/session-prep/useCrossSellBookingLink';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import {
  LuApple,
  LuChevronDown,
  LuChevronUp,
  LuCircleCheck,
  LuPill,
  LuSend,
} from 'react-icons/lu';

const COPIED_LABEL_MS = 2000;
const SECTION_CLASS_NAME =
  'mt-5 flex flex-col gap-4 rounded-[14px] border border-primary-300 bg-primary-100 p-6';

type CrossSellBookingLinkCardProps = {
  sourceAppointmentHealthieId?: string | null;
  patientHealthieId?: string | null;
  /** First name for the GLP-1 headline. Rendered only, never logged. */
  patientFirstName?: string | null;
  glp1Variant: Glp1OfferVariant;
};

export function CrossSellBookingLinkCard({
  sourceAppointmentHealthieId,
  patientHealthieId,
  patientFirstName,
  glp1Variant,
}: CrossSellBookingLinkCardProps): JSX.Element | null {
  const { model, prepareLink, sendLink, isPreparing, isSending } =
    useCrossSellBookingLink({
      sourceAppointmentHealthieId,
      patientHealthieId,
    });
  const { trackEvent } = useAnalytics();
  const toast = useToast({ position: 'top', duration: 3000, isClosable: true });
  const [isCopying, setIsCopying] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout>>();
  // Null until the provider toggles, so the default follows the variant even
  // when the PRE-GLP-1 tag arrives after the first render.
  const [detailsToggledOpen, setDetailsToggledOpen] = useState<boolean | null>(
    null,
  );
  const isDetailsOpen = detailsToggledOpen ?? glp1Variant === 'interested';
  const trackedOfferKey = useRef<string | null>(null);
  const targetProgram =
    'targetProgram' in model ? model.targetProgram : undefined;

  useEffect(
    function trackOfferImpression() {
      if (!targetProgram || !sourceAppointmentHealthieId) return;

      const offerKey = `${sourceAppointmentHealthieId}:${targetProgram}`;
      if (trackedOfferKey.current === offerKey) return;
      trackedOfferKey.current = offerKey;

      trackEvent(EVENT_NAMES.CROSS_SELL_BOOKING_OFFER_VIEWED, {
        targetProgram,
      });
    },
    [sourceAppointmentHealthieId, targetProgram, trackEvent],
  );

  useEffect(function clearCopiedTimeoutOnUnmount() {
    return () => clearTimeout(copiedTimeout.current);
  }, []);

  if (model.status === 'hidden') return null;

  if (model.status === 'loading') {
    return (
      <div className={SECTION_CLASS_NAME}>
        <SessionPrepCardSkeleton rows={3} />
      </div>
    );
  }

  const readyModel = model;
  const isSent = readyModel.status === 'sent';
  const isBusy = isSending || isPreparing || isCopying;
  const isGlp1 =
    readyModel.targetProgram === PlatformCrossSellTargetProgram.Glp_1;
  const glp1Copy = isGlp1
    ? GLP1_OFFER_COPY[isSent ? 'sent' : glp1Variant]
    : undefined;
  const ProgramIcon = isGlp1 ? LuPill : LuApple;
  const copyLabel = isCopied ? 'Copied' : 'Copy link';

  function showSendFailure(): void {
    toast({
      title: 'Booking link was not sent',
      description: 'Copy the link or try again.',
      status: 'error',
    });
  }

  async function handleSendLink(): Promise<void> {
    if (isSent || isBusy) return;

    try {
      const result = await sendLink();
      const outcome = resolveCrossSellSendOutcome(result);

      // Only a delivery this call made is tracked; a channel that reports
      // ALREADY_PROCESSED was already counted by the call that sent it.
      if (result.chatStatus === PlatformCrossSellChatDeliveryStatus.Sent) {
        trackEvent(EVENT_NAMES.CROSS_SELL_BOOKING_LINK_SENT, {
          targetProgram: readyModel.targetProgram,
          channel: 'chat',
        });
      }
      if (
        result.smsStatus === PlatformCrossSellSmsDeliveryStatus.EventAccepted
      ) {
        trackEvent(EVENT_NAMES.CROSS_SELL_BOOKING_LINK_SENT, {
          targetProgram: readyModel.targetProgram,
          channel: 'sms',
        });
      }

      switch (outcome.kind) {
        case 'sent':
          toast({
            title: 'Booking link sent',
            description: 'The link was sent in chat and by text.',
            status: 'success',
          });
          return;
        case 'smsOnly':
          toast({
            title: 'Booking link sent',
            description: 'The link was sent by text.',
            status: 'success',
          });
          return;
        case 'pending':
          toast({
            title: 'Booking link is still sending',
            description: 'Chat or text is still going out.',
            status: 'info',
          });
          return;
        case 'partial':
          toast({
            title: 'Booking link partly sent',
            description: outcome.chatAccepted
              ? 'The link was sent in chat. The text did not send.'
              : 'The link was sent by text. It was not added to chat.',
            status: 'warning',
          });
          return;
        default:
          showSendFailure();
      }
    } catch {
      showSendFailure();
    }
  }

  async function handleCopyLink(): Promise<void> {
    if (isBusy) return;
    setIsCopying(true);

    try {
      const startUrl = await prepareLink();
      await navigator.clipboard.writeText(startUrl);
      trackEvent(EVENT_NAMES.CROSS_SELL_BOOKING_LINK_COPIED, {
        targetProgram: readyModel.targetProgram,
      });
      toast({
        title: 'Booking link copied',
        description: 'The patient-specific link is ready to share.',
        status: 'success',
      });
      setIsCopied(true);
      clearTimeout(copiedTimeout.current);
      copiedTimeout.current = setTimeout(
        () => setIsCopied(false),
        COPIED_LABEL_MS,
      );
    } catch {
      toast({
        title: 'Could not copy booking link',
        description: 'Please try again.',
        status: 'error',
      });
    } finally {
      setIsCopying(false);
    }
  }

  const sendButton = (
    <Button
      type="button"
      data-testid="send-cross-sell-booking-link"
      onClick={handleSendLink}
      disabled={isBusy}
    >
      <LuSend aria-hidden />
      {isSending ? 'Sending link…' : readyModel.sendButtonLabel}
    </Button>
  );

  return (
    <section
      className={SECTION_CLASS_NAME}
      data-testid="cross-sell-booking-link-card"
    >
      <div className="flex flex-col gap-1.5">
        <p className="flex items-center gap-1.5 text-[11px] leading-4 font-bold tracking-[0.05em] text-muted-foreground uppercase">
          <ProgramIcon className="size-3.5 text-primary" aria-hidden />
          {readyModel.programName}
        </p>
        {glp1Copy && (
          <h3 className="text-base leading-6 font-semibold text-foreground">
            <span className="text-primary">
              {patientFirstName?.trim() || 'Your patient'}
            </span>{' '}
            {glp1Copy.headline}
          </h3>
        )}
        <p className="text-sm leading-5 text-muted-foreground">
          {glp1Copy?.description ?? readyModel.description}
        </p>
      </div>

      {isSent ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="flex items-center gap-2 text-sm leading-5 font-medium text-success">
            <LuCircleCheck className="size-4 shrink-0" aria-hidden />
            {readyModel.sentAt
              ? `Link sent · ${format(readyModel.sentAt, "MM/dd/yy 'at' h:mma")}`
              : 'Link sent'}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCopyLink}
            disabled={isBusy}
          >
            {copyLabel}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {isGlp1 ? (
            <TooltipProvider delay={300}>
              <Tooltip>
                <TooltipTrigger render={sendButton} />
                <TooltipContent>
                  Texts and chats the patient a personal link
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            sendButton
          )}
          <Button
            type="button"
            variant="outline"
            onClick={handleCopyLink}
            disabled={isBusy}
          >
            {copyLabel}
          </Button>
        </div>
      )}

      {isGlp1 && (
        <Collapsible
          open={isDetailsOpen}
          onOpenChange={setDetailsToggledOpen}
          className="overflow-hidden rounded-[10px] border border-primary-300 bg-card"
        >
          {/* The whole header row toggles, not just the label. */}
          <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-1.5 px-4 py-3.5 text-left text-sm leading-5 font-semibold text-primary">
            How the program works
            {isDetailsOpen ? (
              <LuChevronUp className="size-4" aria-hidden />
            ) : (
              <LuChevronDown className="size-4" aria-hidden />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="flex flex-col gap-3 px-4 pb-3.5 text-sm leading-5 text-foreground">
            <ol className="flex list-decimal flex-col gap-1 pl-5">
              <li>
                Patient completes a short in-app intake, including an
                eligibility and insurance check.
              </li>
              <li>
                A Berry Street prescriber decides if medication is a fit and
                handles prior authorization.
              </li>
              <li>
                <strong className="font-semibold">
                  You stay their dietitian
                </strong>{' '}
                — MNT visits continue alongside medication.
              </li>
            </ol>
            <div className="flex flex-wrap gap-4 text-[13px] leading-[18px] font-semibold">
              {GLP1_PROGRAM_RESOURCE_LINKS.map(({ href, label }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:text-primary/80 hover:decoration-primary"
                >
                  {label}
                </a>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </section>
  );
}
